import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MedicationTemplate } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/lib/auth";
import { useMedicationTemplatesStore } from "@/lib/stores/medicationTemplates";
import { loadTemplateMedications, deleteMedicationTemplate } from "@/lib/stores/medicationTemplates";

import { getDoctorIdForUser, ensureDoctorProfile } from "@/lib/utils/doctorUtils";
import { Search, Trash2, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface TemplateSearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadTemplate: (medications: any[]) => void;
}

export function TemplateSearchDialog({
  isOpen,
  onClose,
  onLoadTemplate
}: TemplateSearchDialogProps) {
  const { toast } = useToast();
  const { user } = useAuthStore();
  const { templates, fetchTemplates, searchTemplates, removeTemplate, clearTemplates } = useMedicationTemplatesStore();
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  // Load templates when dialog opens
  useEffect(() => {
    if (isOpen && user?.id) {
      // Clear any existing templates to ensure fresh data
      clearTemplates();
      loadTemplates();
    }
  }, [isOpen, user?.id, clearTemplates]);

  // Clear templates when dialog closes
  useEffect(() => {
    if (!isOpen) {
      clearTemplates();
    }
  }, [isOpen, clearTemplates]);

  const loadTemplates = async () => {
    if (!user?.id) {
      return;
    }
    
    setIsLoading(true);
    try {
      // Get or create doctor profile for the user
      let doctorId = await getDoctorIdForUser(user);
      
      if (!doctorId) {
        doctorId = await ensureDoctorProfile(user);
        
        if (!doctorId) {
          toast({
            title: "Error",
            description: "Could not create doctor profile. Please contact administrator.",
            variant: "destructive"
          });
          return;
        }
      }

      await fetchTemplates(doctorId);
    } catch (error) {
      console.error("Error loading templates:", error);
      toast({
        title: "Error loading templates",
        description: `Failed to load your saved templates: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadTemplate = async (templateId: string) => {
    try {
      const medications = await loadTemplateMedications(templateId);
      onLoadTemplate(medications);
      
      toast({
        title: "Template loaded",
        description: "Medications have been added to your prescription"
      });
      
      onClose();
    } catch (error) {
      console.error("Error loading template:", error);
      toast({
        title: "Error loading template",
        description: `Failed to load template medications: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      });
    }
  };

  const handleDeleteTemplate = async (templateId: string, templateName: string) => {
    if (!confirm(`Are you sure you want to delete the template "${templateName}"?`)) {
      return;
    }

    setIsDeleting(templateId);
    try {
      await deleteMedicationTemplate(templateId);
      removeTemplate(templateId);
      
      toast({
        title: "Template deleted",
        description: `Template "${templateName}" has been deleted`
      });
    } catch (error) {
      console.error("Error deleting template:", error);
      toast({
        title: "Error deleting template",
        description: "Failed to delete template",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const filteredTemplates = user?.id 
    ? templates.filter(template => {
        if (searchTerm.trim() === '') return true;
        const term = searchTerm.toLowerCase();
        return template.diagnosis.toLowerCase().includes(term) ||
               template.templateName.toLowerCase().includes(term) ||
               template.medications.some(med => med.name.toLowerCase().includes(term));
      })
    : [];

  // Debug logging commented out to reduce console spam
  // console.log('All templates in store:', templates);
  // console.log('Filtered templates:', filteredTemplates);



  const formatMedicationList = (medications: any[]) => {
    if (medications.length === 0) return "No medications";
    return medications.map(med => med.name).join(", ");
  };


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>My Prescription Templates</DialogTitle>
          <DialogDescription>
            Search and load your saved prescription templates. Click "Load" to add medications from a template to your current prescription.
          </DialogDescription>
        </DialogHeader>
        
        {/* Search */}
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor="template-search" className="text-sm font-medium whitespace-nowrap">
            Search Templates
          </Label>
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              id="template-search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by diagnosis, template name, or medication..."
              className="pl-10"
            />
          </div>
        </div>

        {/* Templates Table */}
        <div className="flex-1 overflow-hidden border rounded-lg">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading templates...
              </div>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="text-sm text-muted-foreground mb-2">
                  {searchTerm ? "No templates found matching your search" : "No templates saved yet"}
                </div>
                {!searchTerm && (
                  <div className="text-xs text-muted-foreground">
                    Create your first template by adding medications and clicking "Save as Template"
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-[50vh]">
              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    <TableHead className="w-[250px]">Template Name</TableHead>
                    <TableHead className="w-[180px]">Diagnosis</TableHead>
                    <TableHead className="w-[80px]">Count</TableHead>
                    <TableHead className="w-[400px]">Medications</TableHead>
                    <TableHead className="w-[140px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTemplates.map((template) => (
                    <TableRow key={template.id} className="hover:bg-muted/50">
                      <TableCell className="font-medium">
                        <div className="space-y-1">
                          <div className="text-sm">{template.templateName}</div>
                          <div className="text-xs text-muted-foreground">
                            Used {template.usageCount} time{template.usageCount !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs">
                          {template.diagnosis}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {template.medications.length}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis" title={formatMedicationList(template.medications)}>
                          {formatMedicationList(template.medications)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleLoadTemplate(template.id)}
                            className="h-8 px-3 text-sm"
                          >
                            Load
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteTemplate(template.id, template.templateName)}
                            disabled={isDeleting === template.id}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          >
                            {isDeleting === template.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
